import { getShiftTotalHours } from "../../../components/StaffTable/utils";
import type { Department } from "../../../contexts/AuthProvider";
import type { NurseInShift } from "../../../utils/models";
import type { ScheduleRow } from "../types";

const HOURS_DECIMAL_FACTOR = 100;

type HoursDepartment = Pick<Department, "department_id" | "breakRulesConfig">;

interface GetShiftScheduledHoursArgs {
	shift: NurseInShift;
	department: HoursDepartment;
	jobManagementEnabled: boolean;
}

/**
 * A shift's hours with the department's break rules deducted — the same figure the Day Schedule staff table
 * shows, so the printout and the screen never disagree.
 */
export const getShiftScheduledHours = ({ shift, department, jobManagementEnabled }: GetShiftScheduledHoursArgs): number => {
	const hours = getShiftTotalHours(shift.startTime, shift.endTime, shift.role, shift.type, department, shift.jobId ? [shift.jobId] : [], jobManagementEnabled);
	// Two decimals keeps quarter-hour breaks exact (11.75) without float noise (11.749999…).
	return Math.round(hours * HOURS_DECIMAL_FACTOR) / HOURS_DECIMAL_FACTOR;
};

interface GetRowScheduledHoursArgs {
	row: ScheduleRow;
	department: HoursDepartment;
	jobManagementEnabled: boolean;
}

/**
 * A row's scheduled hours (`getShiftScheduledHours`, over the row's own times).
 *
 * Gap rows (unfilled slots) return `undefined`: break rules are scoped by employment type, which
 * only a staffed shift has, so there is no correct net figure to show. `compareValues` ranks
 * `undefined` last, which pins gap rows to the bottom like the other staff-derived columns.
 */
export const getRowScheduledHours = ({ row, department, jobManagementEnabled }: GetRowScheduledHoursArgs): number | undefined => {
	if (!row.nurse) return undefined;
	return getShiftScheduledHours({ shift: { ...row.nurse, startTime: row.shiftStart, endTime: row.shiftEnd }, department, jobManagementEnabled });
};
